import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { format } from 'date-fns'; // Import date-fns format function

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats a date object or string into a specified format using browser locale.
 * Handles potential errors gracefully.
 */
export function formatDateSafe(dateInput: Date | string | null | undefined, format: Intl.DateTimeFormatOptions | string = 'PP', fallback: string = 'N/A'): string {
  if (!dateInput) return fallback;
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    // Check for invalid date
    if (isNaN(date.getTime())) {
      return fallback;
    }
    
    // If format is a string like 'PP' or 'PPPp', use common formats (adjust as needed)
    let options: Intl.DateTimeFormatOptions;
    if (format === 'PP') {
        options = { year: 'numeric', month: 'short', day: 'numeric' };
    } else if (format === 'PPPp') {
        options = { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' };
    } else if (format === 'PPPpp') { // NEU: Format mit Sekunden
        options = { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' };
    } else if (typeof format === 'object') {
        options = format;
    } else {
        // Fallback für unbekannte Format-Strings: Datum mit Stunden und Minuten, und eine Warnung
        console.warn(`[formatDateSafe] Unknown format string '${format}', falling back to default date and time format (without seconds).`);
        options = { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' }; 
    }

    return new Intl.DateTimeFormat(navigator.language, options).format(date);
  } catch (error) {
    console.error("Error formatting date:", dateInput, error);
    return fallback; // Return fallback on error
  }
}

// Helper function to get initials from a name
export const getInitials = (name?: string | null): string => {
  if (!name) return 'U'; // Default to 'U' if no name
  
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    // Single word name (e.g., username)
    return parts[0].substring(0, 2).toUpperCase();
  } else if (parts.length > 1) {
    // Multiple words (e.g., first and last name)
    const firstInitial = parts[0][0] || '';
    const lastInitial = parts[parts.length - 1][0] || '';
    return (firstInitial + lastInitial).toUpperCase();
  }
  
  return 'U'; // Fallback
};

// NEW: Function to get Tailwind CSS class based on level tier
export function getLevelTierClass(level: number | null | undefined): string {
  if (level === null || level === undefined) {
    return 'level-tier-zinc'; // Default für unbekanntes Level
  }

  // 10er Schritte mit Hell/Dunkel-Abstufungen
  if (level < 10) return 'level-tier-zinc';     // Grau
  if (level < 20) return 'level-tier-slate';    // Dunkleres Grau
  if (level < 30) return 'level-tier-lime';     // Helles Grün
  if (level < 40) return 'level-tier-emerald';  // Standard Grün
  if (level < 50) return 'level-tier-green';    // Dunkleres Grün
  if (level < 60) return 'level-tier-cyan';     // Helles Türkisblau
  if (level < 70) return 'level-tier-sky';      // Standard Hellblau
  if (level < 80) return 'level-tier-blue';     // Dunkleres Blau
  if (level < 90) return 'level-tier-violet';   // Helles Lila
  if (level < 100) return 'level-tier-purple';   // Standard Lila
  if (level < 110) return 'level-tier-fuchsia';  // Kräftiges Pink/Lila
  if (level < 120) return 'level-tier-pink';     // Standard Pink
  if (level < 130) return 'level-tier-rose';     // Helles Rot/Rosa
  if (level < 140) return 'level-tier-red';      // Standard Rot
  if (level < 150) return 'level-tier-amber';    // Ember

  // Fallback für Level 150+
  return 'level-tier-yellow'; // Gold
}

// Define the structure for a level tier
export interface LevelTier {
  name: string;
  color: string;
  threshold: number;
}

// Define the level tiers array
export const levelTiers: LevelTier[] = [
  { name: 'Zinc', color: 'zinc', threshold: 0 },
  { name: 'Slate', color: 'slate', threshold: 10 },
  { name: 'Lime', color: 'lime', threshold: 20 },
  { name: 'Emerald', color: 'emerald', threshold: 30 },
  { name: 'Green', color: 'green', threshold: 40 },
  { name: 'Cyan', color: 'cyan', threshold: 50 },
  { name: 'Sky', color: 'sky', threshold: 60 },
  { name: 'Blue', color: 'blue', threshold: 70 },
  { name: 'Violet', color: 'violet', threshold: 80 },
  { name: 'Purple', color: 'purple', threshold: 90 },
  { name: 'Fuchsia', color: 'fuchsia', threshold: 100 },
  { name: 'Pink', color: 'pink', threshold: 110 },
  { name: 'Rose', color: 'rose', threshold: 120 },
  { name: 'Red', color: 'red', threshold: 130 },
  { name: 'Ember', color: 'amber', threshold: 140 },
  { name: 'Gold', color: 'yellow', threshold: 150 },
];

// Function to get medal color based on rank
export const getMedalColor = (rank: number): string => {
  if (rank === 1) return 'text-yellow-400'; // Gold
  if (rank === 2) return 'text-slate-400'; // Silver
  if (rank === 3) return 'text-orange-400'; // Bronze
  return 'text-gray-400 dark:text-gray-500'; // Default for other ranks
};

/**
 * Formats a duration in seconds into HH:MM:SS or MM:SS format.
 */
export function formatDurationHMS(totalSeconds: number): string {
  if (isNaN(totalSeconds) || totalSeconds < 0) {
    return '00:00';
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  // Format with leading zeros
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(seconds).padStart(2, '0');

  // Only show hours if there are any
  return hours > 0
    ? `${String(hours).padStart(2, '0')}:${formattedMinutes}:${formattedSeconds}`
    : `${formattedMinutes}:${formattedSeconds}`;
}

/**
 * Converts a snake_case string to camelCase
 * Example: total_words -> totalWords
 */
export function snakeToCamel(str: string): string {
  return str.replace(/([-_][a-z])/g, (group) =>
    group.toUpperCase().replace('-', '').replace('_', '')
  );
}
